import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Checkbox } from './forms/fields/Checkbox';
import OpeningHoursGuard from '../utils/openingHoursGuard';
import { PopUp } from './PopUp';
import { Button } from './Button';
import { Typography } from './Typography';
import { Spacer } from './Spacer';
import { ScreenShareIcon } from './icons';
import tokens from '../tokens/tokens';

declare global {
  interface Window {
    Surfly?: any;
    surflyInitialized?: boolean;
    startSurflySession?: (
      pinCallback: (pin: string) => void,
      sessionEndCallback: () => void
    ) => void;
    endSurflySession?: () => void;
  }
}

const LOAD_SURFLY = process.env.GATSBY_LOAD_SURFLY === 'true';

export const ButtonPinReveal: React.FC<{ companySupportName?: string }> = ({ companySupportName }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [sessionPin, setSessionPin] = useState<string | null>(null);
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [termsAcceptanceError, setTermsAcceptanceError] = useState(false);

  // Ref to track if the validation for starting screen share just failed
  const validationAttemptFailedRef = useRef(false);

  console.log('🔍 ButtonPinReveal Debug - Component state:', {
    modalOpen,
    termsAccepted,
    sessionPin,
    isSessionActive,
    termsAcceptanceError,
    LOAD_SURFLY,
  });

  console.log('🔍 ButtonPinReveal Debug - Will render PopUp with actionLabel "Share screen"');

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setTermsAcceptanceError(false);
    validationAttemptFailedRef.current = false; // Reset ref on any close
    if (!isSessionActive) {
      setTermsAccepted(false);
    }
  }, [
    isSessionActive,
    setModalOpen,
    setTermsAccepted,
    setTermsAcceptanceError,
  ]);

  const onPinGenerated = useCallback((pin: string) => {
    setSessionPin(pin);
    setIsSessionActive(true);
    setModalOpen(false); // This will trigger handleCloseModal via PopUp's onClose eventually or directly
    setTermsAcceptanceError(false);
    validationAttemptFailedRef.current = false;
  }, []);

  const onSessionEnded = useCallback(() => {
    setIsSessionActive(false);
    setSessionPin(null);
    setTermsAccepted(false); // Ensure terms are reset
    setModalOpen(false); // This will trigger handleCloseModal via PopUp's onClose eventually or directly
    setTermsAcceptanceError(false);
    validationAttemptFailedRef.current = false;
    console.log('ButtonPinReveal: Surfly session ended.');
  }, []);

  const handleAttemptStartScreenShare = () => {
    if (!termsAccepted) {
      setTermsAcceptanceError(true);
      validationAttemptFailedRef.current = true;
      return;
    }
    setTermsAcceptanceError(false);
    validationAttemptFailedRef.current = false;

    if (!LOAD_SURFLY) {
      console.error('Surfly is not enabled');
      return;
    }

    // Add a retry mechanism
    const attemptStartSession = (retries = 5) => {
      if (window.startSurflySession) {
        window.startSurflySession(onPinGenerated, onSessionEnded);
      } else if (retries > 0) {
        console.log(
          `Waiting for Surfly to initialize... ${retries} attempts left`
        );
        setTimeout(() => attemptStartSession(retries - 1), 500);
      } else {
        console.error(
          'Error: Surfly session function (window.startSurflySession) is not available after waiting.'
        );
      }
    };

    attemptStartSession();
  };

  // This function is passed to PopUp's onValidateBeforeClose
  // It will be called by PopUp's internal logic before closing.
  const validateBeforeClosingModal = useCallback(() => {
    // If the ref indicates that the last attempt to start sharing failed due to unaccepted terms,
    // prevent the modal from closing.
    if (validationAttemptFailedRef.current) {
      return false; // Prevent closing
    }
    return true; // Allow closing
  }, []); // Empty dependency array means this function instance is stable and always reads the latest ref value.

  const handleCancelSession = () => {
    if (LOAD_SURFLY && window.endSurflySession) {
      window.endSurflySession(); // This should trigger onSessionEnded
    } else {
      onSessionEnded(); // Fallback to ensure UI resets
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && modalOpen) {
        if (validateBeforeClosingModal()) {
          handleCloseModal();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [modalOpen, validateBeforeClosingModal, handleCloseModal]);

  if (!LOAD_SURFLY) {
    return null;
  }

  return (
    <OpeningHoursGuard
      showOnApiError={false} // Default behavior: hide if bank holiday API fails
    >
      {isSessionActive && sessionPin ? (
        <Spacer spacing="half" direction="vertical">
          <Typography variant="paragraph" component="p">
            Session PIN: <strong>{sessionPin}</strong>
          </Typography>
          <Button
            variant="outline"
            onClick={handleCancelSession}
            label="Cancel Session"
          />
        </Spacer>
      ) : (
        <Spacer direction="horizontal" centerVertical spacing="half">
          <PopUp
            modalState={modalOpen ? 'open' : 'closed'}
            onClose={handleCloseModal} // PopUp calls this when it intends to close.
            onValidateBeforeClose={validateBeforeClosingModal} // Crucial for keeping modal open on error
            heading="We're here to help"
            primaryButton={{
              label: 'Start screen share',
              onClick: handleAttemptStartScreenShare,
              disabled: !termsAccepted && !modalOpen, // Disable if terms not accepted, but ensure it's enabled when modal just opens
              iconRight: false,
            }}
            hideClose={false}
            actionIcon={<ScreenShareIcon />}
            actionVariant={tokens.buttonStyle}
            actionIconLarge
          >
            <Spacer spacing="double">
              <Typography component="p" variant="paragraph">
                We would like to assist you in real-time by sharing your screen.
                This will allow us to provide better support.
              </Typography>
              <Checkbox
                id="terms-agree-checkbox"
                label="You agree to allow us to view your screen and interact with the content for the duration of the session"
                checked={termsAccepted}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const isChecked = event.target.checked;
                  setTermsAccepted(isChecked);
                  if (isChecked) {
                    setTermsAcceptanceError(false); // Clear error when checkbox is ticked
                    validationAttemptFailedRef.current = false; // Also clear the ref
                  }
                }}
                // The Checkbox component should display this message when invalid is true.
                errorMessage={
                  termsAcceptanceError
                    ? 'You must accept this statement to proceed'
                    : undefined
                }
                invalid={termsAcceptanceError}
              />
            </Spacer>
          </PopUp>
          <Typography component="p" variant="labelRegular" color="white">
            Share your screen with a member of our {companySupportName ?? 'Agria UK'} Customer Service
            team
          </Typography>
        </Spacer>
      )}
    </OpeningHoursGuard>
  );
};

export default ButtonPinReveal;
